import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ReCaptcha from '../common/ReCaptcha';
import Button from '../common/Button';
import { Mail, Lock, User, Phone, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { logger } from '../../utils/logger';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  
  const recaptchaRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  
  // Validation du mot de passe
  const passwordRequirements = [
    { regex: /.{8,}/, text: 'Au moins 8 caractères', key: 'length' },
    { regex: /[A-Z]/, text: 'Une lettre majuscule', key: 'uppercase' },
    { regex: /[a-z]/, text: 'Une lettre minuscule', key: 'lowercase' },
    { regex: /[0-9]/, text: 'Un chiffre', key: 'number' },
    { regex: /[^A-Za-z0-9]/, text: 'Un caractère spécial', key: 'special' }
  ];
  
  const checkPasswordRequirements = (password) => {
    return passwordRequirements.map(req => ({
      ...req,
      met: req.regex.test(password)
    }));
  };
  
  const passwordChecks = checkPasswordRequirements(formData.password);
  const allRequirementsMet = passwordChecks.every(check => check.met);
  
  // Gérer le changement de champ
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Gérer le changement reCAPTCHA
  const handleRecaptchaChange = (token) => {
    logger.log('✅ reCAPTCHA validé');
    setRecaptchaToken(token);
    
    // Retirer l'erreur reCAPTCHA si elle existe
    if (errors.recaptcha) {
      setErrors(prev => ({
        ...prev,
        recaptcha: ''
      }));
    }
  };
  
  const handleRecaptchaExpired = () => {
    logger.warn('⚠️ reCAPTCHA expiré');
    setRecaptchaToken(null);
    setErrors(prev => ({
      ...prev,
      recaptcha: 'Le reCAPTCHA a expiré, veuillez revalider'
    }));
  };
  
  const handleRecaptchaError = () => {
    logger.error('❌ Erreur reCAPTCHA');
    setRecaptchaToken(null);
    setErrors(prev => ({
      ...prev,
      recaptcha: 'Erreur lors de la validation reCAPTCHA'
    }));
  };
  
  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    // Nom
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est obligatoire';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Le nom doit contenir au moins 2 caractères';
    }
    
    // Email
    if (!formData.email) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    // Téléphone
    if (!formData.phone) {
      newErrors.phone = 'Le numéro de téléphone est obligatoire';
    } else if (!/^[\d\s+()-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Numéro de téléphone invalide';
    }
    
    // Mot de passe
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est obligatoire';
    } else if (!allRequirementsMet) {
      newErrors.password = 'Le mot de passe ne respecte pas tous les critères';
    }
    
    // Confirmation mot de passe
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    // reCAPTCHA
    if (!recaptchaToken) {
      newErrors.recaptcha = 'Veuillez valider le reCAPTCHA';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      logger.warn('⚠️ Validation formulaire échouée');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      logger.log('🔄 Tentative d\'inscription...');
      
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        recaptchaToken: recaptchaToken
      });
      
      logger.log('✅ Inscription réussie');
      setRegistrationSuccess(true);
      
      // Reset formulaire
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        phone: ''
      });
      
      // Reset reCAPTCHA
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      
      // Redirection après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      logger.error('❌ Erreur inscription:', error);
      
      // Reset reCAPTCHA en cas d'erreur
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
      
      if (error.code === 'auth/email-already-in-use') {
        setErrors({ submit: 'Cet email est déjà utilisé' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ submit: 'Email invalide' });
      } else if (error.code === 'auth/weak-password') {
        setErrors({ submit: 'Mot de passe trop faible' });
      } else if (error.message?.includes('reCAPTCHA')) {
        setErrors({ submit: '❌ Échec de la vérification reCAPTCHA. Veuillez réessayer.' });
      } else {
        setErrors({ submit: error.message || 'Une erreur est survenue' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full space-y-8 p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-center">
          <div className="flex justify-center">
            <CheckCircle className="w-16 h-16 text-green-600 dark:text-green-500" />
          </div>
          
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              ✅ Inscription Réussie!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Votre compte a été créé avec succès.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              Un administrateur doit approuver votre compte avant que vous puissiez vous connecter.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-4">
              Redirection vers la page de connexion...
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Créer un compte
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Rejoignez VOMM FJKM Hannover
          </p>
        </div>
        
        <form className="mt-8 space-y-6 bg-white dark:bg-gray-800 p-8 rounded-lg shadow" onSubmit={handleSubmit}>
          {/* Nom complet */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nom complet *
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  errors.name ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="Jean Dupont"
              />
            </div>
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>
          
          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email *
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  errors.email ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="email@exemple.com"
              />
            </div>
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>
          
          {/* Téléphone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Téléphone *
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                  errors.phone ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="+49 151 12345678"
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          </div>
          
          {/* Mot de passe */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Mot de passe *
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-10 py-2 border ${
                  errors.password ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            
            {/* Critères mot de passe */}
            {formData.password && (
              <div className="mt-2 space-y-1">
                {passwordChecks.map((check) => (
                  <div key={check.key} className="flex items-center text-xs">
                    {check.met ? (
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    <span className={check.met ? 'text-green-600' : 'text-gray-600 dark:text-gray-400'}>
                      {check.text}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
          </div>
          
          {/* Confirmation mot de passe */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Confirmer le mot de passe *
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`appearance-none block w-full pl-10 pr-10 py-2 border ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white`}
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
          </div>
          
          {/* reCAPTCHA */}
          <ReCaptcha
            ref={recaptchaRef}
            onChange={handleRecaptchaChange}
            onExpired={handleRecaptchaExpired}
            onError={handleRecaptchaError}
            theme="light"
          />
          
          {errors.recaptcha && (
            <p className="text-red-600 text-sm text-center">
              {errors.recaptcha}
            </p>
          )}
          
          {/* Erreur globale */}
          {errors.submit && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-400">
                {errors.submit}
              </p>
            </div>
          )}
          
          {/* Bouton submit */}
          <Button
            type="submit"
            disabled={isSubmitting || !recaptchaToken}
            className="w-full"
          >
            {isSubmitting ? 'Inscription en cours...' : 'S\'inscrire'}
          </Button>
          
          {/* Lien connexion */}
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vous avez déjà un compte?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-500">
                Se connecter
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
